/**
 * Central de notificações — Fase 2 de `preferencias-e-notificacoes`.
 *
 *   - listar  → POST find/get_notification_inbox        → prepared aggregate
 *   - abrir   → POST find/get_notification_detail       → prepared aggregate
 *   - contar  → POST find/get_notification_unread_count → prepared aggregate
 *   - marcar  → PUT  database/notification_inbox__c     → upsert pelo `_id`
 *
 * O jogador NUNCA vai no corpo: ele sai do `context` do token dentro de cada
 * prepared. Mandá-lo aqui não teria efeito — o `prepare()` sobrescreve — e passar
 * a ideia de que o cliente escolhe de quem é a caixa. O corpo só leva apresentação.
 *
 * Permissões no role `player`: write_find_get_notification_inbox,
 * write_find_get_notification_detail, write_find_get_notification_unread_count,
 * write_database_notification_inbox__c.
 */
import {
  NotificationDetail,
  NotificationDoc,
  NotificationRow,
} from 'pages/Notificacoes/notification-inbox';
import { CommunicationSubject } from 'pages/Preference/communication-preferences';
import { api } from './api';

export interface InboxQuery {
  /** Omitido = todas as famílias. */
  family?: CommunicationSubject;
  /** Só os não lidos: a faixa da Home pede o mais recente com `limit: 1` (HOME-42). */
  status?: 'unread';
  skip?: number;
  limit?: number;
}

/**
 * Falha de rede devolve lista vazia, como o resto dos serviços do projeto. A
 * central é informativa: derrubar a tela por causa dela seria pior do que
 * mostrá-la vazia, e o estado de "não lido" continua no servidor.
 */
export async function getNotificationInbox(query: InboxQuery = {}): Promise<NotificationRow[]> {
  try {
    const { data } = await api.post('find/get_notification_inbox', query);

    if (!Array.isArray(data)) return [];

    return data as NotificationRow[];
  } catch {
    return [];
  }
}

/**
 * Quantos avisos não lidos o jogador tem. Sem nenhum, o `$count` não devolve documento, e isso é 0.
 * Falha, ou um `count` que não seja inteiro maior ou igual a zero, devolve `null` (SINO-05): o sino
 * fica sem número em vez de afirmar um valor que não se sabe.
 */
export async function getNotificationUnreadCount(): Promise<number | null> {
  try {
    const { data } = await api.post('find/get_notification_unread_count', {});

    if (!Array.isArray(data)) return null;
    if (!data.length) return 0;

    const { count } = data[0] ?? {};
    return Number.isInteger(count) && count >= 0 ? count : null;
  } catch {
    return null;
  }
}

export async function getNotificationDetail(id: string): Promise<NotificationDetail | null> {
  try {
    const { data } = await api.post('find/get_notification_detail', { id });
    const doc = Array.isArray(data) ? data[0] : data;

    return (doc as NotificationDetail) ?? null;
  } catch {
    return null;
  }
}

/**
 * `doc` tem de ser o documento COMPLETO — o PUT é substituição, e campo ausente é
 * campo apagado. Use `toReadPayload`, que monta a forma certa e, tão importante
 * quanto, deixa de fora `subject`, `content` e `inapp`, que vêm de `$lookup` e não
 * pertencem ao documento.
 *
 * Best-effort: falhar em marcar como lido não pode impedir o colaborador de ler o
 * aviso. Ele reabre e a marcação tenta de novo.
 */
export async function markAsRead(doc: NotificationDoc): Promise<void> {
  try {
    await api.put('database/notification_inbox__c', doc);
  } catch {
    // silencioso por decisão de design: a leitura é do colaborador, não do servidor
  }
}
